import styles from "./style.module.scss";
import { PrintPage } from "../../components/PrintPage";
import { Main, Content } from "../../components/Layout";
import useWrapper from "../../hooks/useWrapper";
import { InputTextArea } from "../../components/Input";
import { useState, useEffect, useMemo, useRef } from "react";
import Button from "../../components/Button";
import api from "../../services/api";
import TableResponsive from "../../components/TableResponsive";
import Loading from "../../components/Loading";
import { MessageBaloon } from "../../components/AttendanceChat";
import useFormat from "../../hooks/utils/useFormat";
import { ChartsBar, ChartsLegend, ChartsTooltipCard, ChartsTooltipMultiCard, ChartsKpi } from "../../components/Charts";
import { Cell, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart } from "recharts";
import { MonthTick } from "../../components/ChartTick";
import colorFromIndex from "../../hooks/utils/colorFromIndex";
import { downloadReport } from "../../util/functions";

export default function ReportsAI() {
  const { config, me } = useWrapper();
  const { formatDate, formatReal } = useFormat();
  const [newMessage, setnewMessage] = useState({ text: "" });

  const [messageIA, setmessageIA] = useState(
    "Olá! Eu sou a inteligência artificial do Formou. Você pode me fazer perguntas sobre os seus dados, e eu vou tentar te ajudar a encontrar as respostas!",
  );
  const [aiResp, setaiResp] = useState();
  const [lastQuestion, setlastQuestion] = useState();
  const [isThinking, setisThinking] = useState();
  const [hoverIndex, sethoverIndex] = useState(-1);
  const [barTooltipPos, setbarTooltipPos] = useState(null);
  const barChartRef = useRef(null);

  useEffect(() => {
    sethoverIndex(-1);
  }, [aiResp]);

  useEffect(() => {
    if (hoverIndex === -1 || !barChartRef.current) {
      setbarTooltipPos(null);
      return;
    }
    const _el = barChartRef.current.querySelectorAll(".recharts-bar-rectangle")?.[hoverIndex];
    if (!_el) {
      setbarTooltipPos(null);
      return;
    }
    const _elRect = _el.getBoundingClientRect();
    const _containerRect = barChartRef.current.getBoundingClientRect();
    setbarTooltipPos({
      x: _elRect.left - _containerRect.left + _elRect.width / 2,
      y: _elRect.top - _containerRect.top,
    });
  }, [hoverIndex, aiResp]);
  const sendMessage = async () => {
    if (!newMessage?.text) return;
    setisThinking(true);
    setmessageIA(null);
    setaiResp(null);

    try {
      const { data: response } = await api.post(
        "ai-query",
        {
          question: newMessage?.text,
        },
        config,
      );

      const _ai = response?.openai_return;
      const _data = response?.data?.map(__row =>
        _ai?.selected_fields?.reduce((__acc, __field) => {
          return {
            ...__acc,
            ...formatFieldObj(__row?.[__field], _ai?.field_types?.[__field], __field),
          };
        }, {}),
      );

      setaiResp({
        data: _data,
        ai: _ai,
      });

      setlastQuestion(newMessage?.text);
      setnewMessage({ ...newMessage, text: "" });
      setmessageIA(response?.openai_return?.explanation || "Aqui está o que eu encontrei:");
    } catch (__error) {
      setmessageIA(
        "Algo errado aconteceu. Tente novamente mais tarde. Entre em contato com o suporte se o problema persistir.",
      );
      console.log("error", __error);
    } finally {
      setisThinking(false);
    }
  };

  const formatFieldObj = (__value, __type, __field) => {
    if (__type === "date") {
      return {
        [__field]: __value,
        [`${__field}_formatted`]: formatDate(__value, 2),
      };
      // return {
      //   [__field]: {
      //     raw: __value,
      //     formated: formatDate(__value, 2),
      //   },
      // };
    }
    if (__type === "currency") {
      return {
        [__field]: __value,
        [`${__field}_formatted`]: formatReal(__value),
      };
      // return {
      //   [__field]: {
      //     raw: __value,
      //     formated: formatReal(__value),
      //   },
      // };
    }
    return {
      [__field]: __value,
    };
  };

  useEffect(() => {
    aiResp && console.log("openAiResponse", aiResp);
  }, [aiResp]);

  const reportView = useMemo(() => {
    const _type = aiResp?.ai?.presentation?.type;
    const _config = aiResp?.ai?.presentation?.config || {};
    const _fieldTypes = aiResp?.ai?.field_types || {};

    if (_type === "text" || !aiResp?.ai?.sql) return null;

    const buildReports = (__labelField, __valueField) => {
      const _reports =
        aiResp?.data?.map((__row, __i) => ({
          label: __row?.[`${__labelField}_formatted`] || __row?.[__labelField] || "-",
          value: Number(__row?.[__valueField]) || 0,
          color: colorFromIndex(__i),
        })) || [];
      const _total = _reports.reduce((__acc, __r) => __acc + (__r.value || 0), 0);
      const _format_value = _fieldTypes?.[__valueField] === "currency" ? "real" : "";
      return { reports: _reports, total: _total, format_value: _format_value };
    };

    const buildSeriesPivot = (__xField, __yField, __seriesField) => {
      const _xKey = `${__xField}_formatted`;
      const _byX = new Map();
      const _seriesNames = [];
      aiResp?.data?.forEach(__row => {
        const _xRaw = __row?.[__xField];
        const _xDisplay = __row?.[_xKey] || _xRaw;
        const _seriesValue = __row?.[__seriesField] ?? "-";
        if (!_seriesNames.includes(_seriesValue)) _seriesNames.push(_seriesValue);
        if (!_byX.has(_xRaw)) _byX.set(_xRaw, { [__xField]: _xDisplay });
        _byX.get(_xRaw)[_seriesValue] = Number(__row?.[__yField]) || 0;
      });

      let _chartData = Array.from(_byX.entries())
        .sort((__a, __b) => (__a[0] > __b[0] ? 1 : __a[0] < __b[0] ? -1 : 0))
        .map(([, __row]) => __row);

      const _lastValues = {};
      _chartData = _chartData.map(__row => {
        const _filledRow = { ...__row };
        _seriesNames.forEach(__name => {
          if (_filledRow[__name] === undefined) {
            _filledRow[__name] = _lastValues[__name] ?? 0;
          } else {
            _lastValues[__name] = _filledRow[__name];
          }
        });
        return _filledRow;
      });

      return { chartData: _chartData, seriesNames: _seriesNames };
    };

    const _isMultiLine = _type === "line_chart" && !!_config?.series_field;
    const _seriesPivot = _isMultiLine
      ? buildSeriesPivot(_config?.x_field, _config?.y_field, _config?.series_field)
      : null;
    const _yIsCurrency = _fieldTypes?.[_config?.y_field] === "currency";

    const _head = _isMultiLine
      ? [
          { data: aiResp?.ai?.field_labels?.[_config?.x_field] || _config?.x_field },
          ..._seriesPivot.seriesNames.map(__name => ({ data: __name })),
        ]
      : aiResp?.ai?.selected_fields.map(__field => ({
          data: aiResp?.ai?.field_labels?.[__field] || __field,
        }));
    const _body = _isMultiLine
      ? _seriesPivot.chartData.map(__row => ({
          data: [
            { element: __row?.[_config?.x_field] || "-" },
            ..._seriesPivot.seriesNames.map(__name => ({
              element: _yIsCurrency ? formatReal(__row?.[__name]) : __row?.[__name],
            })),
          ],
        }))
      : aiResp?.data?.map(__row => ({
          data: aiResp?.ai?.selected_fields?.map(__field => ({
            element: __row?.[`${__field}_formatted`] || __row?.[__field] || "-",
          })),
        })) || [];

    return {
      type: _type,
      config: _config,
      fieldTypes: _fieldTypes,
      buildReports,
      isMultiLine: _isMultiLine,
      seriesPivot: _seriesPivot,
      yIsCurrency: _yIsCurrency,
      head: _head,
      body: _body,
    };
  }, [aiResp, formatReal]);

  const downloadCSV = () => {
    const _rows = [["--- CHAT I.A. ---"], [""]];
    if (lastQuestion) _rows.push(["Pergunta", lastQuestion]);
    if (messageIA) _rows.push(["Resposta", messageIA]);
    _rows.push([""], [""]);
    if (reportView?.body?.length > 0) {
      _rows.push(["--- DADOS ---"], [""]);
      _rows.push(reportView.head.map(__col => __col.data));
      reportView.body.forEach(__row => {
        _rows.push(__row.data.map(__cell => __cell.element ?? ""));
      });
      _rows.push([""], [""]);
    }
    _rows.push(["--- RODAPÉ ---"], [""]);
    _rows.push([`Relatório Gerado por ${me.username}`]);
    _rows.push([formatDate(new Date(), 4)]);
    downloadReport(_rows, "relatorio-chat-ia");
  };

  return (
    <PrintPage has_logo={true} signed_by={me.username}>
      <Main>
        <Content>
          <div className={styles.ReportsAI}>
            <div className="d-flex justify-content-between align-items-start">
              <h2 className="mini-title">
                Chat I.A. <span className="small-label">(Beta)</span>
              </h2>
              {!!aiResp && (
                <div className="d-flex">
                  <PrintPage.PrintButton label="Gerar PDF" />
                  {reportView?.body?.length > 0 && (
                    <Button className="ml-3 -hide-on-print" onClick={downloadCSV}>
                      Gerar CSV
                    </Button>
                  )}
                </div>
              )}
            </div>
            <p className="small-label mb-4">
              Esta funcionalidade está em fase de testes e pode apresentar instabilidades. Se os dados forem muito
              importantes, recomendamos aguardar a versão final ou conferir os dados manualmente no sistema.
            </p>
          </div>

          <div className="-hide-on-print">
            <InputTextArea
              required={true}
              disabled={!!isThinking}
              value={newMessage?.text || ""}
              onChange={__ev => {
                setnewMessage({
                  ...newMessage,
                  text: __ev.target.value,
                });
              }}
              placeholder="Pergunte algo sobre os seus dados, por exemplo: 'Qual foi a turma com a maior receita no último ano?'"
            />
            <div className="d-flex justify-content-end flex-grow-1 mt-2">
              <Button
                className="ml-2"
                loading={!!isThinking}
                disabled={!newMessage?.text && !(newMessage?.files?.length > 0)}
                type="submit"
                onClick={sendMessage}
              >
                Enviar Comando
              </Button>
            </div>
          </div>

          <div className="mb-4 mt-4 -hide-on-print">
            <MessageBaloon
              message={messageIA || "Pensando..."}
              sender_is_self={false}
              name={"Inteligência Artificial"}
              hide_avatar={false}
              downloadFile={() => {}}
            />
          </div>

          {!isThinking ? (
            <>
              {/* {true && ( */}
              {aiResp && reportView && (
                <>
                  {(() => {
                    const {
                      type: _type,
                      config: _config,
                      fieldTypes: _fieldTypes,
                      buildReports,
                      isMultiLine: _isMultiLine,
                      seriesPivot: _seriesPivot,
                      yIsCurrency: _yIsCurrency,
                      head: _head,
                      body: _body,
                    } = reportView;

                    return (
                      <>
                        {_type === "bar_chart" &&
                          (() => {
                            const { reports: _reports, total: _total, format_value: _format_value } = buildReports(
                              _config?.x_field,
                              _config?.y_field,
                            );
                            const _hoverReport = hoverIndex !== -1 ? _reports[hoverIndex] : null;
                            return (
                              <>
                                <div ref={barChartRef} style={{ position: "relative" }}>
                                  <ComposedChart
                                    style={{ width: "100%", height: "240px" }}
                                    data={aiResp?.data || []}
                                    margin={{
                                      top: 5,
                                      right: 0,
                                      left: 0,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid horizontal vertical strokeDasharray="3 3" />
                                    <XAxis dataKey={_config?.x_field} tick={false} />
                                    <YAxis width="auto" tickCount={11} tick={{ fontSize: 10 }} />
                                    <Bar type="monotone" dataKey={_config?.y_field}>
                                      {_reports.map((__report, __i) => (
                                        <Cell
                                          key={__i}
                                          fill={__report.color}
                                          fillOpacity={hoverIndex === __i ? 1 : 0.6}
                                          onMouseEnter={() => sethoverIndex(__i)}
                                          onMouseLeave={() => sethoverIndex(-1)}
                                        />
                                      ))}
                                    </Bar>
                                  </ComposedChart>
                                  {_hoverReport && barTooltipPos && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: barTooltipPos.x,
                                        top: barTooltipPos.y,
                                        transform: "translate(-50%, calc(-100% - 10px))",
                                        pointerEvents: "none",
                                        zIndex: 10,
                                      }}
                                    >
                                      <ChartsTooltipCard
                                        label={_hoverReport.label}
                                        value={
                                          _format_value === "real"
                                            ? formatReal(_hoverReport.value)
                                            : _hoverReport.value.toFixed(2)
                                        }
                                        percent={`${(_hoverReport.value ? (_hoverReport.value / _total) * 100 : 0).toFixed(2)}%`}
                                        color={_hoverReport.color}
                                      />
                                    </div>
                                  )}
                                </div>
                                <ChartsLegend
                                  reports={_reports}
                                  format_value={_format_value}
                                  total={_total}
                                  hover={hoverIndex}
                                  onHover={sethoverIndex}
                                />
                              </>
                            );
                          })()}
                        {_type === "pie_chart" &&
                          (() => {
                            const { reports: _reports, format_value: _format_value } = buildReports(
                              _config?.label_field,
                              _config?.value_field,
                            );
                            return (
                              <ChartsBar
                                reports={_reports}
                                title={aiResp?.ai?.presentation?.title}
                                type={aiResp?.ai?.field_labels?.[_config?.value_field] || ""}
                                format_value={_format_value}
                              />
                            );
                          })()}
                        {_type === "kpi_card" &&
                          (() => {
                            const _valueField = _config?.value_field;
                            const _row = aiResp?.data?.[0];
                            const _value =
                              _row?.[`${_valueField}_formatted`] ||
                              (_fieldTypes?.[_valueField] === "currency"
                                ? formatReal(_row?.[_valueField])
                                : _row?.[_valueField]) ||
                              "-";
                            return (
                              <ChartsKpi
                                label={_config?.label || aiResp?.ai?.field_labels?.[_valueField] || ""}
                                value={_value}
                              />
                            );
                          })()}
                        {_type === "line_chart" &&
                          (() => {
                            const _format_value = _yIsCurrency ? "real" : "";
                            const _chartData = _isMultiLine ? _seriesPivot.chartData : aiResp?.data || [];
                            const _seriesNames = _isMultiLine ? _seriesPivot.seriesNames : null;

                            const _dataLength = _chartData.length;
                            const _maxTicks = 10;
                            const _tickInterval =
                              _dataLength > _maxTicks ? Math.ceil(_dataLength / _maxTicks) - 1 : 0;

                            return (
                              <>
                                <ComposedChart
                                  style={{ width: "100%", height: "240px" }}
                                  data={_chartData}
                                  margin={{
                                    top: 5,
                                    right: 0,
                                    left: 0,
                                    bottom: 30,
                                  }}
                                >
                                  <CartesianGrid horizontal vertical strokeDasharray="3 3" />
                                  <XAxis
                                    dataKey={_config?.x_field}
                                    tick={<MonthTick angle={_tickInterval > 0 ? -35 : 0} />}
                                    interval={_tickInterval}
                                    height={50}
                                  />
                                  <YAxis
                                    width="auto"
                                    tickCount={11}
                                    tick={{ fontSize: 10 }}
                                    tickFormatter={_format_value === "real" ? formatReal : undefined}
                                  />
                                  <Tooltip
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload?.length) return null;
                                      const _items = payload.map(__entry => ({
                                        label: __entry.name,
                                        value: _format_value === "real" ? formatReal(__entry.value) : __entry.value,
                                        color: __entry.color,
                                      }));
                                      return <ChartsTooltipMultiCard title={label} items={_items} />;
                                    }}
                                  />
                                  {_seriesNames ? (
                                    _seriesNames.map((__name, __i) => (
                                      <Line
                                        key={__name}
                                        type="monotone"
                                        dataKey={__name}
                                        name={__name}
                                        stroke={colorFromIndex(__i)}
                                        strokeWidth={hoverIndex === __i ? 2.5 : 1.5}
                                        strokeOpacity={hoverIndex === __i ? 1 : 0.6}
                                        dot={{ r: 3 }}
                                        onMouseEnter={() => sethoverIndex(__i)}
                                        onMouseLeave={() => sethoverIndex(-1)}
                                      />
                                    ))
                                  ) : (
                                    <Line
                                      type="monotone"
                                      dataKey={_config?.y_field}
                                      name={aiResp?.ai?.field_labels?.[_config?.y_field] || _config?.y_field}
                                      stroke={colorFromIndex(0)}
                                      dot={{ r: 3 }}
                                    />
                                  )}
                                </ComposedChart>
                                {_seriesNames && (
                                  <ChartsLegend
                                    reports={_seriesNames.map((__name, __i) => ({
                                      label: __name,
                                      value: 1,
                                      color: colorFromIndex(__i),
                                    }))}
                                    hideValue
                                    hover={hoverIndex}
                                    onHover={sethoverIndex}
                                  />
                                )}
                              </>
                            );
                          })()}
                        {_type !== "kpi_card" && (
                          <TableResponsive
                            className="mt-4"
                            table={{
                              head: _head || [],
                              body: _body || [],
                            }}
                          />
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </>
          ) : (
            <>
              <Loading />
            </>
          )}
        </Content>
      </Main>
    </PrintPage>
  );
}
